import { tva } from '@gluestack-ui/utils/nativewind-utils'
import React from 'react'
import { twMerge } from 'tailwind-merge'

import { Pressable } from '../pressable'
import { Text } from '../text'
import { type TextSize, type TextWeight } from '../text/variants'

export type LinkState = 'default' | 'hover' | 'pressed' | 'visited' | 'disabled'

export type LinkProps = {
    children: React.ReactNode
    onPress?: () => void
    /**
     * Force a visual state (for the story matrix / preview). When omitted the
     * link is live: hover and press resolve via CSS on web.
     */
    state?: LinkState
    /** Render with the visited ink. Ignored when `state` is set. */
    visited?: boolean
    isDisabled?: boolean
    size?: TextSize
    /** Label weight. Defaults to `semiBold`; table links run at the cell weight. */
    weight?: TextWeight
    className?: string
    testID?: string
}

const linkText = tva({
    variants: {
        state: {
            default: 'text-aos-text-link',
            hover: 'text-aos-text-link-hover',
            pressed: 'text-aos-text-link-pressed',
            visited: 'text-aos-text-link-visited',
            disabled: 'text-aos-text-link',
            live: 'text-aos-text-link group-hover/link:text-aos-text-link-hover group-active/link:text-aos-text-link-pressed',
        },
    },
    defaultVariants: { state: 'live' },
})

export const Link: React.FC<LinkProps> = ({
    children,
    onPress,
    state,
    visited,
    isDisabled,
    size = 'body',
    weight = 'semiBold',
    className,
    testID,
}) => {
    const resolvedState: LinkState | 'live' =
        state ?? (isDisabled ? 'disabled' : visited ? 'visited' : 'live')

    return (
        <Pressable
            onPress={isDisabled ? undefined : onPress}
            accessibilityRole='link'
            disabled={isDisabled}
            className={twMerge('group/link', isDisabled ? 'opacity-40' : '', className)}
            testID={testID}
        >
            <Text size={size} weight={weight} className={linkText({ state: resolvedState })}>
                {children}
            </Text>
        </Pressable>
    )
}

Link.displayName = 'Link'
